import PageHeader from "../../../components/layout/PageHeader.jsx";
import { useState, useEffect, useCallback, useContext } from 'react';
import { useFirestoreGeneralOrderBy, useFirestoreIn, useFirestoreGeneral } from '../../../firebase/useFirestore.js';
import Location from "../../../hooks/Location"
import { db, timestamp } from '../../../firebase/config';
import { v4 as uuid } from 'uuid';
import { client } from "../../../hooks/Client"
import { useHistory } from "react-router-dom";
import firebase from '../../../firebase/compat';
import useSettings from "../../../hooks/Settings";
import { measurementMethodLabels } from "../../../helpers/dataHelpers/measurementMethods";
import MultipleOptionsField from "../../../components/events/MultipleOptionsField.jsx";
import SubTargetgroupsEvents from "../../../components/events/SubTargetgroupsEvents.jsx";
import { Auth } from '../../../StateManagment/Auth'
import { pickEventDate } from '../../../helpers/eventHelpers/dateFieldResults.js'
import buildRegistrationDocs from '../../../helpers/eventHelpers/buildRegistrationDocs.js'
import { isFlagOff } from '../../../helpers/isFlagOn';
import scaleRange from '../../../helpers/questionnaire/scaleRange';
import visibleEventFields from '../../../helpers/events/visibleEventFields';
import registrationDateHint from '../../../helpers/events/registrationDateHint';

const AddEvent = () => {
  // Context 
   const [auth] = useContext(Auth)

  // ── State
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [datesByFieldId, setDatesByFieldId] = useState({}); // { [fieldId]: 'yyyy-mm-dd' }
  const [outputsArray, setOutputsArray] = useState([]);
  const [personasArray, setPersonasArray] = useState([]);
  const [outputParticipantsByKey, setOutputParticipantsByKey] = useState({});
  const [participantsByFieldId, setParticipantsByFieldId] = useState({}); // { [fieldId]: [personaId] }
  const [targetgroupsArray, setTargetgroupsArray] = useState([]); // flat list of selected targetgroup IDs (for convenience)
  const [targetgroupsByFieldId, setTargetgroupsByFieldId] = useState({}); // { [fieldId]: [targetgroupId] }
  const [subTargetgroupsByFieldId, setSubTargetgroupsByFieldId] = useState({}); // { [fieldId]: { [targetgroupId]: [subId] } }
  const [openResponses, setOpenResponses] = useState({}); // { [fieldId]: { type:'open', values:[{value}] } }
  const [responses, setResponses] = useState({});
  const [activityId, setActivityId] = useState('');
  const [activityTargetgroups, setActivityTargetgroups] = useState([{ ID: '', Name: '' }]);

  // ── Hooks
  const eventId = Location()[3];
  const history = useHistory();
  const personaOn = useSettings().personasOn;

  // ── Firestore
  const events       = useFirestoreGeneral('EventForms', 'ID', eventId || '');
  const eventFields  = useFirestoreGeneralOrderBy('EventFields', 'EventId', eventId || '', 'Position', 'asc');
  const outputs      = useFirestoreGeneralOrderBy('Outputs', 'ActivityID', activityId || '', 'Position', 'asc');
  const activities   = useFirestoreGeneral('Activities', 'ID', activityId || '');
  const personas     = useFirestoreGeneral('Personas', 'Status', 'active');
  const targetgroups =  useFirestoreIn('Stakeholders', 'ID', activityTargetgroups || [{ ID: '', Name: '' }]);

  // ── Prefill event meta
  useEffect(() => {
    events && events.forEach(event => {
      setTitle(event.Title || '');
      setDescription(event.Description || '');
      setActivityId(event.ActivityID || '');
    });
  }, [events]);

  // Set activity targetgroup
  useEffect(() => {
    activities && activities.forEach(activity => {
      setActivityTargetgroups(activity.Targetgroup || [{ ID: '', Name: '' }]);
    });
  }, [activities]);

  // ── Date fields
  // A form can hold several date fields (Startdatum + Einddatum). They are kept
  // per field; the event's own moment is the first one in form order.
  const dateFieldIds = (eventFields || []).filter(field => field.Type === 'date').map(field => field.ID);
  const eventDate = pickEventDate(datesByFieldId, dateFieldIds);

  // ── Save all buffered data
  const saveEvent = async () => {
    const fallbackNewId = uuid();
    const currentEventId = eventId || fallbackNewId;
    const sessionId = uuid()

    try {
      // Dezelfde opbouw als het formulier via de link en de Cloud Function
      // saveEventRegistration: één bron (buildRegistrationDocs).
      const docs = buildRegistrationDocs({
        buffers: {
          outputsArray,
          responses,
          openResponses,
          datesByFieldId,
          participantsByFieldId,
          targetgroupsByFieldId,
          subTargetgroupsByFieldId,
        },
        meta: {
          client,
          activityId,
          eventId: currentEventId,
          sessionId,
          user: auth.ID,
          timestamp: eventDate ? firebase.firestore.Timestamp.fromDate(new Date(eventDate)) : timestamp,
        },
        newId: uuid,
      });

      await Promise.all(docs.map(doc => db.collection('EventResults').add(doc)));

      history.push(`/${client}/events`);
    } catch (error) {
      console.error('Error saving event or results:', error);
    }
  };

  // Date (buffered per field, so Startdatum and Einddatum stay independent)
  const dateHandler = (e) => {
    const fieldid = e.target.dataset.fieldid;
    const value = e.target.value;
    setDatesByFieldId(prev => ({ ...prev, [fieldid]: value }));
  };

  // Participants (checkboxes)
  const participantsHandler = (e) => {
    const fieldid = e.target.dataset.fieldid;
    const value = e.target.value;
    const checked = e.target.checked;

    setParticipantsByFieldId(prev => {
      const set = new Set(prev[fieldid] || []);
      if (checked) set.add(value); else set.delete(value);
      return { ...prev, [fieldid]: Array.from(set) };
    });

    // keep your convenience arrays
    setPersonasArray(prev => checked ? [...prev, value] : prev.filter(v => v !== value));
  };

  // Numeric outputs (one number per OutputID)
  const outputsHandler = (e) => {
    const fieldId  = e.target.dataset.fieldid;
    const outputId = e.target.dataset.outputid;
    const value    = e.target.value;
    const outputKey = `${fieldId}-${outputId}`;

    setOutputsArray(prev => {
      const idx = prev.findIndex(o => o.fieldId === fieldId && o.outputId === outputId);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], value };
        return next;
      }
      return [...prev, { fieldId, outputId, value, participantIds: outputParticipantsByKey[outputKey] || [] }];
    });
  };

  const outputParticipantsHandler = (e) => {
    const fieldId = e.target.dataset.fieldid;
    const outputId = e.target.dataset.outputid;
    const personaId = e.target.value;
    const checked = e.target.checked;
    const outputKey = `${fieldId}-${outputId}`;

    setOutputParticipantsByKey(prev => {
      const set = new Set(prev[outputKey] || []);
      if (checked) set.add(personaId); else set.delete(personaId);
      return { ...prev, [outputKey]: Array.from(set) };
    });

    setOutputsArray(prev => {
      const idx = prev.findIndex(o => o.fieldId === fieldId && o.outputId === outputId);
      const participantIds = checked
        ? Array.from(new Set([...(prev[idx]?.participantIds || []), personaId]))
        : (prev[idx]?.participantIds || []).filter(id => id !== personaId);

      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], participantIds };
        return next;
      }

      return [...prev, { fieldId, outputId, value: '', participantIds }];
    });
  };

  // Targetgroups (checkboxes)
  const targetgroupsHandler = (e) => {
    const fieldid = e.target.dataset.fieldid;
    const tgId = e.target.value;
    const checked = e.target.checked;

    setTargetgroupsByFieldId(prev => {
      const set = new Set(prev[fieldid] || []);
      if (checked) set.add(tgId); else set.delete(tgId);
      return { ...prev, [fieldid]: Array.from(set) };
    });

    // convenience flat list
    setTargetgroupsArray(prev => checked ? [...prev, tgId] : prev.filter(v => v !== tgId));

    // if unchecking a TG, also clear its subTG selections for this field
    if (!checked) {
      setSubTargetgroupsByFieldId(prev => {
        const fieldMap = { ...(prev[fieldid] || {}) };
        if (fieldMap[tgId]) {
          delete fieldMap[tgId];
        }
        return { ...prev, [fieldid]: fieldMap };
      });
    }
  };

  // Open text
  const openHandler = (e) => {
    const fieldid = e.target.dataset.fieldid;
    const value = e.target.value;
    setOpenResponses(prev => ({
      ...prev,
      [fieldid]: { type: 'open', values: [{ value }] }
    }));
  };

  // Scale
  const scaleChange = useCallback((field, value) => {
    setResponses(prev => ({
      ...prev,
      [field.ID]: { type: 'scale', values: [{ value }] }
    }));
  }, []);

  // Number
  const numberHandler = (e) => {
    const fieldid = e.target.dataset.fieldid;
    const value = e.target.value;
    setResponses(prev => ({
      ...prev,
      [fieldid]: { type: 'number', values: [{ value }] }
    }));
  };

  // Multiple (from MultipleOptionsField)
  const handleMultipleChange = useCallback((updates) => {
    // updates: { fieldId, type, add?, remove?, replace? }
    setResponses(prev => {
      const existing = prev[updates.fieldId]?.values || [];
      let nextValues = existing;

      if (updates.replace) {
        nextValues = [{ optionId: updates.replace.optionId, value: updates.replace.value }];
      } else {
        if (updates.add?.length) {
          nextValues = [
            ...existing,
            ...updates.add.filter(a => !existing.find(x => x.optionId === a.optionId))
          ];
        }
        if (updates.remove?.length) {
          const removeIds = new Set(updates.remove.map(r => r.optionId));
          nextValues = existing.filter(x => !removeIds.has(x.optionId));
        }
      }

      return {
        ...prev,
        [updates.fieldId]: { type: updates.type, values: nextValues }
      };
    });
  }, []);

  // Subtargetgroups toggle (from child)
  const handleSubTargetToggle = useCallback((fieldId, targetgroupId, subId, checked) => {
    setSubTargetgroupsByFieldId(prev => {
      const fieldMap = { ...(prev[fieldId] || {}) };
      const set = new Set(fieldMap[targetgroupId] || []);
      if (checked) set.add(subId); else set.delete(subId);
      fieldMap[targetgroupId] = Array.from(set);
      return { ...prev, [fieldId]: fieldMap };
    });
  }, []);

  // AI-CONTEXT: See docs/features/output-registratie-per-keer-of-totaal.md (ADR-070)
  // One date per submission; a period total belongs on the period's last day.
  // The hook yields '' while loading, so check for a real list first.
  const asksOutputs = Array.isArray(eventFields) && eventFields.some((field) => field.Type === 'outputs');
  const dateHint = asksOutputs ? registrationDateHint(outputs) : null;

  // ── Field renderer
  const fieldType = (field) => {
    switch (field.Type) {
      case 'date':
        return (
          <div className='event-builder-type-item-container event-builder-type-item-date-container'>
            <input type="date" data-fieldid={field.ID} value={datesByFieldId[field.ID] || ''} onChange={dateHandler} />
            {dateHint && <p className='event-date-hint'>{dateHint}</p>}
          </div>
        );

      case 'participants':
        return (
          <div className='event-builder-type-item-container event-builder-type-item-multiple-container'>
            {personas && personas.map(persona => {
              const checked = (participantsByFieldId[field.ID] || []).includes(persona.ID);
              return (
                <div key={persona.ID} id='add-event-persona-item-container'>
                  <input
                    type="checkbox"
                    data-fieldid={field.ID}
                    value={persona.ID}
                    checked={checked}
                    onChange={participantsHandler}
                  />
                  <label>{persona.Name}</label>
                </div>
              );
            })}
          </div>
        );

      case 'outputs':
        return (
          <div className='event-builder-type-item-container'>
            {outputs && outputs.map(output => {
              const currentOutput = outputsArray.find(o => o.fieldId === field.ID && o.outputId === output.ID);
              const aantalFilled = Number(currentOutput?.value || 0) > 0;
              const showPersonas = personaOn === 'flex' && !isFlagOff(output.LinkParticipantsToOutputs);
              // Outputs measured elsewhere (ADR-011) are read-only here — entering
              // them in the event form too would double-count.
              const method = output.MeasurementMethod || 'event_form';
              const measuredElsewhere = method !== 'event_form';

              if (measuredElsewhere) {
                return (
                  <div key={output.ID} id='add-event-output-item-container'>
                    <div className='event-output-row'>
                      <p>{output.Title}</p>
                      <p className='event-output-measured-elsewhere'>Wordt gemeten via {measurementMethodLabels[method] || method}</p>
                    </div>
                  </div>
                );
              }

              return (
                <div key={output.ID} id='add-event-output-item-container'>
                  <div className='event-output-row'>
                    <p>{output.Title}</p>
                    <input
                      className='event-output-input'
                      type="number"
                      min="0"
                      placeholder={0}
                      data-outputid={output.ID}
                      data-fieldid={field.ID}
                      onChange={outputsHandler}
                    />
                  </div>
                  <p className='event-output-aantal-helper'>Vul eerst het totale aantal in.</p>

                  {showPersonas && (
                    <div className={`event-output-personas-section${aantalFilled ? '' : ' gated'}`}>
                      <div className='event-output-personas-heading'>
                        <h4>Wie waren aanwezig?</h4>
                        <span className='event-output-personas-pill'>Optioneel</span>
                      </div>
                      <p className='event-output-personas-helper'>
                        {aantalFilled
                          ? 'Aanvinken verandert het aantal hierboven niet.'
                          : 'Vul eerst het aantal hierboven in om aan te vinken.'}
                      </p>
                      <div className='event-output-personas-container'>
                        {personas && personas.map(persona => {
                          const outputKey = `${field.ID}-${output.ID}`;
                          const checked = (outputParticipantsByKey[outputKey] || []).includes(persona.ID);

                          return (
                            <div key={`${output.ID}-${persona.ID}`} className='event-output-persona-option'>
                              <input
                                type="checkbox"
                                data-fieldid={field.ID}
                                data-outputid={output.ID}
                                value={persona.ID}
                                checked={checked}
                                disabled={!aantalFilled}
                                onChange={outputParticipantsHandler}
                              />
                              <label>{persona.Name}</label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );

      case 'targetgroups':
        return (
          <div className='event-builder-type-item-container event-builder-type-item-multiple-container'>
            {targetgroups && targetgroups.map(tg => {
              const tgChecked = (targetgroupsByFieldId[field.ID] || []).includes(tg.ID);
              const selectedSubs = (subTargetgroupsByFieldId[field.ID]?.[tg.ID]) || [];
              return (
                <div key={tg.ID} id='add-event-targetgroup-item-container'>
                  <input
                    type="checkbox"
                    data-fieldid={field.ID}
                    value={tg.ID}
                    checked={tgChecked}
                    onChange={targetgroupsHandler}
                  />
                  <label>{tg.Organisation}</label>

                  {/* Sub targetgroups for this TG */}
                  <SubTargetgroupsEvents
                    fieldId={field.ID}
                    targetgroupId={tg.ID}
                    selected={selectedSubs}
                    onToggle={handleSubTargetToggle}
                    disabled={!tgChecked}
                  />
                </div>
              );
            })}
          </div>
        );

      case 'open':
        return (
          <div className='event-builder-type-item-container'>
            <textarea
              placeholder="Schrijf hier je antwoord"
              data-fieldid={field.ID}
              onChange={openHandler}
            />
          </div>
        );

      case 'number':
        return (
          <div className='event-builder-type-item-container'>
            <input type="number" placeholder={0} data-fieldid={field.ID} onChange={numberHandler} />
          </div>
        );

      case 'scale': {
        // Formuliervelden worden aangemaakt zonder bereik; die tonen bewust 0 t/m 10.
        const values = scaleRange(field, { fallbackStart: 0, fallbackEnd: 10 });
        return (
          <div className='event-builder-type-item-container'>
            <div id='scale-container'>
              {values.map(val => (
                <div key={`${field.ID}-${val}`} id='question-type-label-container'>
                  <input
                    type="radio"
                    name={field.ID}
                    value={val}
                    onChange={() => scaleChange(field, String(val))}
                  />
                  <label htmlFor={`${field.ID}-${val}`}>{val}</label>
                </div>
              ))}
            </div>
          </div>
        );
      }

      case 'multiple-one':
        return (
          <MultipleOptionsField
            field={field}
            type="one"
            onChange={handleMultipleChange}
          />
        );

      case 'multiple-multiple':
        return (
          <MultipleOptionsField
            field={field}
            type="multiple"
            onChange={handleMultipleChange}
          />
        );

      default:
        return 'Geen type geselecteerd';
    }
  };

  return (
    <div className="main">
      <div className="main-container">
        <PageHeader 
          heading={'Resultaten toevoegen'}
          description={`Voeg hier een nieuw resultaat toe.`}
          AI={false}
        />

        {events && events.map(event => (
          <div className="table-container dashboard-container" key={event.ID}>
            {activities && activities.map(activity => (
              <p className="add-event-activity-title" key={activity.ID}>
                Activiteit: <strong>{activity.Activity}</strong>
              </p>
            ))}
            <h1>{event.Title}</h1>
            <div className="event-description-container">
              <p>{event.Description}</p>
            </div>
          </div>
        ))}

        {eventFields && visibleEventFields(eventFields, { outputs, personaOn }).map(field => (
          <div className="table-container dashboard-container" key={field.ID}>
            <h2>{field.Title}</h2>
            {fieldType(field)}
          </div>
        ))}

        <div className="button-container">
          <button className="button" onClick={saveEvent}>Opslaan</button>
        </div>
      </div>
    </div>
  );
};

export default AddEvent;
